// Hubtel Mobile Money Integration
// API Configuration

const HUBTEL_CONFIG = {
  API_ID: 'XY8wyk',
  API_KEY: 'f0c123a8e9484ecf97fafad29b874374',
  MERCHANT_ACCOUNT: '2037710',
  BASE_URL: 'https://payproxyapi.hubtel.com',
  PREAPPROVAL_URL: 'https://preapproval.hubtel.com/api/v2',
  RMP_URL: 'https://rmp.hubtel.com/merchantaccount'
};

// Generate Base64 Auth Header
const getAuthHeader = () => {
  const credentials = btoa(`${HUBTEL_CONFIG.API_ID}:${HUBTEL_CONFIG.API_KEY}`);
  return `Basic ${credentials}`;
};

// Alternative auth header format - try with merchant account
const getAltAuthHeader = () => {
  const credentials = btoa(`${HUBTEL_CONFIG.MERCHANT_ACCOUNT}:${HUBTEL_CONFIG.API_KEY}`);
  return `Basic ${credentials}`;
};

// Generate unique reference
export const generateReference = (prefix = 'REF') => {
  return `${prefix}-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
};

/**
 * ONLINE CHECKOUT - Initiate payment and redirect to Hubtel
 * @param {Object} paymentData
 * @param {number} paymentData.amount - Amount in GHS
 * @param {string} paymentData.description - Payment description
 * @param {string} paymentData.clientReference - Unique reference
 * @param {string} paymentData.callbackUrl - Callback URL for status updates
 * @param {string} paymentData.returnUrl - Return URL after payment
 * @param {string} paymentData.cancellationUrl - Cancellation URL
 */
export const initiateOnlineCheckout = async (paymentData) => {
  // Debug: Log the request
  console.log('[Hubtel] Initiating checkout with:', {
    amount: paymentData.amount,
    description: paymentData.description,
    clientReference: paymentData.clientReference
  });
  console.log('[Hubtel] Auth header:', getAuthHeader().substring(0, 20) + '...');
  
  const payload = {
    totalAmount: parseFloat(paymentData.amount),
    description: paymentData.description,
    callbackUrl: paymentData.callbackUrl,
    returnUrl: paymentData.returnUrl,
    merchantAccountNumber: HUBTEL_CONFIG.MERCHANT_ACCOUNT,
    cancellationUrl: paymentData.cancellationUrl,
    clientReference: paymentData.clientReference
  };

  try {
    // Try with standard auth first
    let response = await fetch(`${HUBTEL_CONFIG.BASE_URL}/items/initiate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': getAuthHeader(),
        'Accept': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    // Get raw response for debugging
    let rawText = await response.text();
    console.log('[Hubtel] Response status:', response.status);
    console.log('[Hubtel] Response body:', rawText.substring(0, 500));

    // If 401, try alternative auth format
    if (response.status === 401 && rawText.includes('Unauthorized')) {
      console.log('[Hubtel] Trying alternative auth...');
      response = await fetch(`${HUBTEL_CONFIG.BASE_URL}/items/initiate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': getAltAuthHeader(),
          'Accept': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      rawText = await response.text();
      console.log('[Hubtel] Alt auth response:', response.status);
    }

    // Check if response is OK before parsing JSON
    if (!response.ok) {
      // Handle 401 specifically - authentication failure
      if (response.status === 401) {
        console.log('[Hubtel] 401 received, trying alternative method...');
        return await initiateCheckoutAlternative(paymentData);
      }
      return {
        success: false,
        error: `Server error: ${response.status} ${response.statusText}`,
        data: null
      };
    }

    const text = rawText;
    if (!text || text.trim() === '') {
      return {
        success: false,
        error: 'Empty response from server',
        data: null
      };
    }

    let data;
    try {
      data = JSON.parse(text);
    } catch (parseError) {
      return {
        success: false,
        error: 'Invalid JSON response from server',
        rawResponse: text
      };
    }
    
    if (response.ok && data.responseCode === '0000') {
      return {
        success: true,
        checkoutUrl: data.data?.checkoutUrl,
        transactionId: data.data?.transactionId,
        clientReference: paymentData.clientReference,
        data: data
      };
    } else {
      return {
        success: false,
        error: data.message || 'Failed to initiate checkout',
        data: data
      };
    }
  } catch (error) {
    console.error('[Hubtel] Online Checkout Error:', error);
    return {
      success: false,
      error: error.message || 'Network error'
    };
  }
};

/**
 * Alternative checkout method - try direct checkout page redirect
 * This constructs a checkout URL that redirects to Hubtel's hosted checkout
 */
const initiateCheckoutAlternative = async (paymentData) => {
  console.log('[Hubtel] Trying alternative checkout method...');
  
  // Try direct checkout page redirect
  const checkoutPageUrl = `https://pay.hubtel.com/`;
  
  // Build checkout redirect URL with all parameters
  const params = new URLSearchParams({
    m: HUBTEL_CONFIG.MERCHANT_ACCOUNT,
    a: paymentData.amount.toString(),
    d: paymentData.description.substring(0, 100),
    r: paymentData.clientReference
  });
  
  // Try old style checkout
  try {
    const oldStyleUrl = `https://pay.hubtel.com/${HUBTEL_CONFIG.MERCHANT_ACCOUNT}?amount=${paymentData.amount}&desc=${encodeURIComponent(paymentData.description)}`;
    console.log('[Hubtel] Trying old style checkout:', oldStyleUrl);
    
    return {
      success: true,
      checkoutUrl: oldStyleUrl,
      clientReference: paymentData.clientReference,
      isAlternative: true
    };
  } catch (e) {
    console.log('[Hubtel] Alt checkout failed:', e.message);
  }
  
  // If alternative also fails, return a helpful error
  return {
    success: false,
    error: 'Unable to connect to payment service. Please verify your account is active or try another payment method.',
    isAuthError: true,
    data: null
  };
};

/**
 * DIRECT DEBIT - Step 1: Initiate Preapproval
 * @param {Object} preapprovalData
 * @param {string} preapprovalData.customerMsisdn - Customer phone (e.g., 233XXXXXXXXX)
 * @param {string} preapprovalData.channel - 'mtn-gh-direct-debit' or 'vodafone-gh-direct-debit'
 * @param {string} preapprovalData.callbackUrl - Callback URL
 * @param {string} preapprovalData.clientReferenceId - Unique reference
 */
export const initiatePreapproval = async (preapprovalData) => {
  const payload = {
    clientReferenceId: preapprovalData.clientReferenceId,
    customerMsisdn: preapprovalData.customerMsisdn,
    channel: preapprovalData.channel,
    callbackUrl: preapprovalData.callbackUrl
  };

  try {
    const response = await fetch(
      `${HUBTEL_CONFIG.PREAPPROVAL_URL}/merchant/${HUBTEL_CONFIG.MERCHANT_ACCOUNT}/preapproval/initiate`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': getAuthHeader()
        },
        body: JSON.stringify(payload)
      }
    );

    const data = await response.json();

    if (response.ok && data.responseCode === '2000') {
      return {
        success: true,
        hubtelPreApprovalId: data.data?.hubtelPreApprovalId,
        clientReferenceId: data.data?.clientReferenceId,
        verificationType: data.data?.verificationType,
        otpPrefix: data.data?.otpPrefix,
        data: data
      };
    } else {
      return {
        success: false,
        error: data.message || 'Failed to initiate preapproval',
        data: data
      };
    }
  } catch (error) {
    console.error('[Hubtel] Preapproval Initiate Error:', error);
    return {
      success: false,
      error: error.message || 'Network error'
    };
  }
};

/**
 * DIRECT DEBIT - Step 2: Verify OTP
 * @param {Object} otpData
 * @param {string} otpData.customerMsisdn - Customer phone
 * @param {string} otpData.hubtelPreApprovalId - Preapproval ID from step 1
 * @param {string} otpData.clientReferenceId - Client reference
 * @param {string} otpData.otpCode - Full OTP code (e.g., HNRM-8852)
 */
export const verifyPreapprovalOTP = async (otpData) => {
  const payload = {
    customerMsisdn: otpData.customerMsisdn,
    hubtelPreApprovalId: otpData.hubtelPreApprovalId,
    clientReferenceId: otpData.clientReferenceId,
    otpCode: otpData.otpCode
  };

  try {
    const response = await fetch(
      `${HUBTEL_CONFIG.PREAPPROVAL_URL}/merchant/${HUBTEL_CONFIG.MERCHANT_ACCOUNT}/preapproval/verifyotp`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': getAuthHeader()
        },
        body: JSON.stringify(payload)
      }
    );

    const data = await response.json();

    if (response.ok) {
      return {
        success: true,
        data: data
      };
    } else {
      return {
        success: false,
        error: data.message || 'OTP verification failed',
        data: data
      };
    }
  } catch (error) {
    console.error('[Hubtel] OTP Verification Error:', error);
    return {
      success: false,
      error: error.message || 'Network error'
    };
  }
};

/**
 * DIRECT DEBIT - Step 3: Charge Customer
 * @param {Object} chargeData
 * @param {string} chargeData.customerMsisdn - Customer phone
 * @param {string} chargeData.channel - 'mtn-gh-direct-debit' or 'vodafone-gh-direct-debit'
 * @param {number} chargeData.amount - Amount to charge
 * @param {string} chargeData.description - Charge description
 * @param {string} chargeData.callbackUrl - Callback URL
 * @param {string} chargeData.clientReference - Unique reference
 * @param {string} chargeData.customerName - Customer name (optional)
 * @param {string} chargeData.customerEmail - Customer email (optional)
 */
export const chargeCustomer = async (chargeData) => {
  const payload = {
    CustomerName: chargeData.customerName || 'Customer',
    CustomerMsisdn: chargeData.customerMsisdn,
    CustomerEmail: chargeData.customerEmail || '',
    Channel: chargeData.channel,
    Amount: parseFloat(chargeData.amount),
    PrimaryCallbackUrl: chargeData.callbackUrl,
    Description: chargeData.description,
    ClientReference: chargeData.clientReference
  };

  try {
    const response = await fetch(
      `${HUBTEL_CONFIG.RMP_URL}/merchants/${HUBTEL_CONFIG.MERCHANT_ACCOUNT}/receive/mobilemoney`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': getAuthHeader()
        },
        body: JSON.stringify(payload)
      }
    );

    const data = await response.json();

    if (response.ok) {
      return {
        success: true,
        data: data
      };
    } else {
      return {
        success: false,
        error: data.message || 'Charge failed',
        data: data
      };
    }
  } catch (error) {
    console.error('[Hubtel] Charge Error:', error);
    return {
      success: false,
      error: error.message || 'Network error'
    };
  }
};

/**
 * Check Preapproval Status
 * @param {string} clientReferenceId - Client reference ID
 */
export const checkPreapprovalStatus = async (clientReferenceId) => {
  try {
    const response = await fetch(
      `${HUBTEL_CONFIG.PREAPPROVAL_URL}/merchant/${HUBTEL_CONFIG.MERCHANT_ACCOUNT}/preapproval/${clientReferenceId}/status`,
      {
        headers: {
          'Authorization': getAuthHeader()
        }
      }
    );

    const data = await response.json();
    return {
      success: response.ok,
      data: data
    };
  } catch (error) {
    console.error('[Hubtel] Status Check Error:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Cancel Preapproval
 * @param {string} customerMsisdn - Customer phone number
 */
export const cancelPreapproval = async (customerMsisdn) => {
  try {
    const response = await fetch(
      `${HUBTEL_CONFIG.PREAPPROVAL_URL}/merchant/${HUBTEL_CONFIG.MERCHANT_ACCOUNT}/preapproval/${customerMsisdn}/cancel`,
      {
        headers: {
          'Authorization': getAuthHeader()
        }
      }
    );

    const data = await response.json();
    return {
      success: response.ok,
      data: data
    };
  } catch (error) {
    console.error('[Hubtel] Cancel Error:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Helper to format phone number to Hubtel format (233XXXXXXXXX)
export const formatPhoneToHubtel = (phone) => {
  // Remove all non-digits
  let cleaned = phone.replace(/\D/g, '');
  
  // If starts with 0, replace with 233
  if (cleaned.startsWith('0')) {
    cleaned = '233' + cleaned.substring(1);
  }
  
  // If doesn't start with 233, add it
  if (!cleaned.startsWith('233')) {
    cleaned = '233' + cleaned;
  }
  
  return cleaned;
};

// Helper to get channel from carrier
export const getChannelFromCarrier = (carrier, isDirectDebit = false) => {
  const channels = {
    'mtn': isDirectDebit ? 'mtn-gh-direct-debit' : 'mtn-gh',
    'vodafone': isDirectDebit ? 'vodafone-gh-direct-debit' : 'vodafone-gh',
    'airteltigo': isDirectDebit ? 'airteltigo-gh-direct-debit' : 'airteltigo-gh'
  };
  return channels[carrier] || channels['mtn'];
};

export default {
  initiateOnlineCheckout,
  initiatePreapproval,
  verifyPreapprovalOTP,
  chargeCustomer,
  checkPreapprovalStatus,
  cancelPreapproval,
  generateReference,
  formatPhoneToHubtel,
  getChannelFromCarrier,
  HUBTEL_CONFIG
};
